import React, { useState, ChangeEvent, useEffect } from 'react';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';

import './styles.css';

interface DataTableProps {
    columns: string[];
    rows?: string[][];
    editor?: boolean;
    index?: number;
    addButtonText?: string;
    onResponseChange: (value: any) => void;
    radioColumns: number[];
    inlineEditor?: boolean;
    customAdd?: () => void;
    customEdit?: (rowIndex: number) => void;
    dragDropEnabled?: boolean;
    localizer: IStringLocalizer;
    requiredColumns?: boolean[];
}

const DataTable: React.FC<DataTableProps> = ({ columns, rows, editor = true, index, addButtonText, onResponseChange, radioColumns, inlineEditor = true, customAdd, customEdit, dragDropEnabled = false, localizer, requiredColumns }) => {
    const inlineEditorEnabled = inlineEditor ?? true;
    const [rowsValue, setRowsValue] = useState(rows || []);
    const [draggedRowsValue, setDraggedRowsValue] = useState(rows || []);
    const [draggedRowStartIndex, setDraggedRowStartIndex] = useState(-1);
    const [draggedRowOverIndex, setDraggedRowOverIndex] = useState(-1);

    useEffect(() => {
        setRowsValue(rows || []);
    }, [rows]);

    const handleInputChange = (rowIndex: number, columnIndex: number, event: ChangeEvent<HTMLInputElement>) => {
        if (rowsValue) {
            let updatedMatrix = [...rowsValue];

            updatedMatrix[rowIndex][columnIndex] = event.target.value;
            setRowsValue(updatedMatrix);
            onResponseChange(updatedMatrix);
        }
    }

    const handleRadioInputChange = (rowIndex: number, columnIndex: number, event: ChangeEvent<HTMLInputElement>) => {
        if (rowsValue) {
            let updatedMatrix = [...rowsValue];

            radioColumns.forEach(radioColumn => updatedMatrix[rowIndex][radioColumn] = "No");
            updatedMatrix[rowIndex][columnIndex] = event.target.value;
            setRowsValue(updatedMatrix);
            onResponseChange(updatedMatrix);
        }
    }

    const deleteRow = (rowIndex: number) => {
        if (rowsValue) {
            let updatedRows = rowsValue.filter((_, index) => index !== rowIndex);

            setRowsValue(updatedRows);
            onResponseChange(updatedRows);
        }
    }

    const addRow = () => {
        if (customAdd) {
            return customAdd();
        }

        let newRow = Array(columns.length).fill('');

        if (rowsValue) {
            let updatedRows = [...rowsValue, newRow];

            setRowsValue(updatedRows);
            onResponseChange(updatedRows);
        } else {
            setRowsValue(newRow);
            onResponseChange(newRow);
        }
    }

    const trimMatrix = (row: string[]) => {
        if (row.length < columns.length) {
            // push empty cells to fill gap
            for (let i = columns.length - row.length; i-- > 0;) {
                row.push("");
            }
        } else if (row.length > columns.length) {
            // clip overflow cells
            while (row.length > columns.length) {
                row.pop();
            }
        }
    }

    const onDragStart = (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
        setDraggedRowStartIndex(index);
    }

    const onDragOver = (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
        if (!rowsValue || draggedRowStartIndex < 0) {
            return;
        }

        const newRowsValue = [...rowsValue];
        const draggedRow = rowsValue[draggedRowStartIndex];

        newRowsValue.splice(draggedRowStartIndex, 1);
        newRowsValue.splice(index, 0, draggedRow);

        setDraggedRowOverIndex(index);
        setDraggedRowsValue(newRowsValue);
    }

    const onDragEnd = () => {
        setRowsValue(draggedRowsValue);
        setDraggedRowOverIndex(-1);
        onResponseChange(draggedRowsValue);
    }

    return (
        <div className="generic-table-parent data-table-parent">
            <button className="btn btn-default add-data-table-row-button m-b-10" onClick={addRow} disabled={!editor} data-table-index="${index}">{addButtonText ?? localizer.localizeString("Add New Row")}</button>
            <div className="table-container">
                <table className={`table toxnot-table-v2 m-b-0 ${dragDropEnabled ? "table-drag-drop" : ""}`}>
                    <thead>
                        <tr>
                            {dragDropEnabled && <th></th>}
                            {columns.map((columnValue, key) => <th key={key}>{columnValue}{requiredColumns && requiredColumns[key] && <span className="p-relative m-l-5"><span className="text-danger p-absolute required-asterisk">*</span></span>}</th>)}
                            {editor ? (<th></th>) : null}
                        </tr>
                    </thead>
                    <tbody className="data-table" data-data-table-index={index}>
                        {rowsValue?.map(
                            (row, rowIndex) => {
                                trimMatrix(row);

                                return (
                                    <tr className={rowIndex === draggedRowOverIndex ? "dragged-row-over" : ""} key={rowIndex} draggable={dragDropEnabled} onDragStart={e => onDragStart(e, rowIndex)} onDragOver={e => onDragOver(e, rowIndex)} onDragEnd={e => onDragEnd()}>
                                        {dragDropEnabled && <td className="drag-indicator-cell"><i className="material-icons">drag_indicator</i></td>}
                                        {!inlineEditorEnabled && row?.map((colValue, colIndex) =>
                                            <td key={colIndex}>
                                                {colValue}
                                            </td>
                                        )}
                                        {inlineEditorEnabled && row?.map((colValue, colIndex) =>
                                            <td key={colIndex}>
                                                {radioColumns.indexOf(colIndex) !== -1 ?
                                                    <input type="radio" onChange={(event) => handleRadioInputChange(rowIndex, colIndex, event)}
                                                        name={rowIndex.toString()}
                                                        value="Yes"
                                                        disabled={!editor}
                                                        checked={colValue.toLowerCase() == "yes"} /> :
                                                    <input type="text" onChange={(event) => handleInputChange(rowIndex, colIndex, event)} className="form-control question-answer-data-table-cell-input" value={colValue} disabled={!editor}></input>
                                                }
                                            </td>
                                        )}
                                        {editor ?
                                            (<td className="text-right">
                                                {!inlineEditorEnabled && customEdit && <button className="btn btn-default m-r-10" data-table-row-index={rowIndex} onClick={() => customEdit(rowIndex)}><span className="glyphicon glyphicon-pencil"></span></button>}
                                                <button className="btn btn-toxnot-danger remove-data-table-row-button" data-table-row-index={rowIndex} onClick={() => deleteRow(rowIndex)}><span className="glyphicon glyphicon-remove"></span></button>
                                            </td>)
                                            : null
                                        }
                                    </tr>
                                )
                            }
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default DataTable;